import React, { Suspense, lazy, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";

const SupportChatWidget = lazy(() => import("./SupportChatWidget"));
const TutorialTriggerButton = lazy(() =>
  import("./tutorial/TutorialTriggerButton").then((module) => ({
    default: module.TutorialTriggerButton,
  }))
);
const DeferredToaster = lazy(() =>
  import("./ui/sonner").then((module) => ({ default: module.Toaster }))
);

function useDeferredUiMount(delayMs = 2500) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return undefined;

    const markMounted = () => setMounted(true);
    let timeoutId = null;
    let idleId = null;

    if (typeof window !== "undefined") {
      timeoutId = window.setTimeout(markMounted, delayMs);

      const hasIdleCallback = typeof window.requestIdleCallback === "function";
      if (hasIdleCallback) {
        idleId = window.requestIdleCallback(markMounted, {
          timeout: delayMs + 1200,
        });
      }

      window.addEventListener("pointerdown", markMounted, {
        once: true,
        passive: true,
      });
      window.addEventListener("keydown", markMounted, { once: true });
      window.addEventListener("focusin", markMounted, { once: true });
    }

    return () => {
      if (typeof window === "undefined") return;

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      if (idleId && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }

      window.removeEventListener("pointerdown", markMounted);
      window.removeEventListener("keydown", markMounted);
      window.removeEventListener("focusin", markMounted);
    };
  }, [delayMs, mounted]);

  return mounted;
}

export default function Layout({
  children,
  showFooter = true,
  footerMode = "full",
  showNavbar = true,
}) {
  const shouldLoadDeferredUi = useDeferredUiMount();
  const location = useLocation();
  const pathname = String(location?.pathname || "").toLowerCase();

  const isRoleDashboardPath =
    pathname === "/dashboard" ||
    (pathname.startsWith("/dashboard/") &&
      !pathname.startsWith("/dashboard/admin"));

  const usePricingBackdrop =
    pathname.startsWith("/find-a-tutor") ||
    pathname.startsWith("/subjects") ||
    pathname.startsWith("/about") ||
    pathname.startsWith("/resources") ||
    isRoleDashboardPath;

  return (
    <div
      className={`min-h-screen flex flex-col relative ${
        usePricingBackdrop ? "solid-surface-ui" : ""
      }`}
    >
      {usePricingBackdrop ? (
        <>
          <div className="pricing-wave-bg" aria-hidden />
          <div className="pricing-wave-cover" aria-hidden />
        </>
      ) : (
        <div className="aurora-bg" aria-hidden />
      )}

      {showNavbar && <Navbar />}

      <main className="flex-1 relative">
        {children ?? (
          <Suspense
            fallback={
              <div className="container-aus py-14">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="glass-card h-40 skeleton" />
                  ))}
                </div>
              </div>
            }
          >
            <Outlet />
          </Suspense>
        )}
      </main>

      {showFooter && <Footer compact={footerMode === "compact"} />}

      {shouldLoadDeferredUi ? (
        <Suspense fallback={null}>
          <SupportChatWidget />
          {isRoleDashboardPath ? <TutorialTriggerButton /> : null}
          <DeferredToaster
            position="top-right"
            richColors
            toastOptions={{
              style: {
                background: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255,255,255,0.7)",
                borderRadius: "14px",
                color: "#0B1426",
              },
            }}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
